import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Copy text, and say whether it worked. The Clipboard API is refused in some
 * places a click still counts (embedded browsers, some permission setups), so
 * the older select-and-copy runs first, while the click is still fresh, and
 * the Clipboard API is the fallback.
 *
 * `from` is the clicked element: the temporary field goes in its dialog, if
 * any, so a dialog's focus trap doesn't pull focus (and the selection) away.
 */
export async function copyToClipboard(text: string, from?: Element | null): Promise<boolean> {
  if (legacyCopy(text, from)) return true;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function legacyCopy(text: string, from?: Element | null): boolean {
  const host = from?.closest('[role="dialog"]') || document.body;
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.setAttribute('aria-hidden', 'true');
  Object.assign(field.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
  });
  const before = document.activeElement as HTMLElement | null;
  host.appendChild(field);
  let ok = false;
  try {
    field.focus();
    field.select();
    field.setSelectionRange(0, text.length);
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  field.remove();
  before?.focus?.();
  return ok;
}

/**
 * Copy buttons with feedback: `copy(key, text, event)` copies, and `state(key)`
 * is 'copied' or 'failed' for 2 seconds after (or null), so a button can say so.
 */
export function useCopy() {
  const [last, setLast] = useState<{ key: string; ok: boolean } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = useCallback(
    async (key: string, text: string, event?: { currentTarget: Element }) => {
      const ok = await copyToClipboard(text, event?.currentTarget);
      setLast({ key, ok });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setLast(null), 2000);
      return ok;
    },
    []
  );
  const state = useCallback(
    (key: string): 'copied' | 'failed' | null =>
      last?.key === key ? (last.ok ? 'copied' : 'failed') : null,
    [last]
  );
  return { copy, state };
}
